import { useState, useMemo } from 'react'

import api from '../../../utils/api'
import Input from '../../../shared/components/form/input'
import Button from '../../../shared/components/Button/Button'
import FormLayout from '../../../shared/components/FormLayout/FormLayout'
import buildCategoryOptions from '../../../shared/functions/buildCategoryOptions'
import useFlashMessage from '../../../hooks/useFlashMessage'
import AllocationsField, { allocationsMismatch } from '../AllocationsField'

import '../Finance.css'

// Lançamento avulso, independente de Compra/Pedido/Produção — mesmo endpoint serve Contas a
// Pagar e a Receber, o "type" vem fixo de quem abriu o form (Payable.js/Receivable.js).
// editingEntry: quando informado, abre em modo de edição — se já tiver parcela paga, valor
// total e parcelamento ficam travados (só descrição/contraparte/rateio são editáveis), pra não
// desalinhar parcelas que já foram baixadas.
function FormFinanceEntry({ type, categories, editingEntry, btCancelAction, handleOnSuccess }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const hasPaidParcel = !!editingEntry?.parcels?.some((p) => p.status === 'pago')
    const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories])

    function initialAllocations() {
        if (!editingEntry) return [{ category: null, value: '' }]
        return editingEntry.allocations.map((a) => ({
            category: categoryOptions.find((o) => o.value === (a.category?._id || a.category)) || null,
            value: String(a.value),
        }))
    }

    const [description, setDescription] = useState(editingEntry?.description || '')
    const [counterpartName, setCounterpartName] = useState(editingEntry?.counterpart?.name || '')
    const [totalValue, setTotalValue] = useState(editingEntry ? String(editingEntry.totalValue) : '')
    const [allocations, setAllocations] = useState(initialAllocations)
    const [installments, setInstallments] = useState('1')
    const [intervalDays, setIntervalDays] = useState('30')
    const [firstDueDate, setFirstDueDate] = useState('')

    async function submit(e) {
        e.preventDefault()

        if (!description) {
            setFlashMessage('Informe a descrição', 'error')
            return
        }
        const value = parseFloat(totalValue)
        if (!value || value <= 0) {
            setFlashMessage('Informe um valor total válido', 'error')
            return
        }
        if (allocations.some((a) => !a.category)) {
            setFlashMessage('Selecione a categoria de cada linha do rateio', 'error')
            return
        }
        if (allocationsMismatch(allocations, totalValue)) {
            setFlashMessage('A soma do rateio entre categorias precisa bater com o valor total', 'error')
            return
        }

        const counterpart = counterpartName ? { kind: type === 'pagar' ? 'provider' : 'client', name: counterpartName } : undefined
        const allocationsBody = allocations.map((a) => ({ category: a.category.value, value: parseFloat(a.value) || 0 }))

        let msgType = 'success'
        let data
        if (editingEntry) {
            data = await api.patch(`/finance/entry/${editingEntry._id}`, { description, counterpart, allocations: allocationsBody }, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })
        } else {
            if (!firstDueDate) {
                setFlashMessage('Informe a data do primeiro vencimento', 'error')
                return
            }
            data = await api.post('/finance/entry/new', {
                type,
                description,
                counterpart,
                totalValue: value,
                allocations: allocationsBody,
                installments: parseInt(installments) || 1,
                intervalDays: parseInt(intervalDays) || 30,
                firstDueDate,
            }, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })
        }

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') handleOnSuccess()
    }

    return (
        <FormLayout
            title={editingEntry ? 'Editar Lançamento' : (type === 'pagar' ? 'Nova Conta a Pagar' : 'Nova Conta a Receber')}
            onSubmit={submit}
            actions={
                <>
                    <Button variant="save" type="submit">{editingEntry ? 'Salvar Alterações' : 'Criar Lançamento'}</Button>
                    <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                </>
            }
        >
            {hasPaidParcel && (
                <p className="fin-row-origin">Esse lançamento já tem parcela paga — valor total e parcelamento não podem mais ser alterados aqui. Pra corrigir, cancele e crie um novo lançamento.</p>
            )}

            <div className="fl-row">
                <div className="fl-col-wide form-group">
                    <Input text="Descrição" type="text" name="description" placeholder="Ex.: Material de limpeza" handleOnChange={(e) => setDescription(e.target.value)} value={description} />
                </div>
                <div className="fl-col-mid form-group">
                    <Input text={type === 'pagar' ? 'Fornecedor (opcional)' : 'Cliente (opcional)'} type="text" name="counterpartName" placeholder="Nome" handleOnChange={(e) => setCounterpartName(e.target.value)} value={counterpartName} />
                </div>
            </div>

            <div className="form-group">
                <Input text="Valor total (R$)" type="number" name="totalValue" placeholder="0.00" handleOnChange={(e) => setTotalValue(e.target.value)} value={totalValue} disabled={!!editingEntry} />
            </div>

            <AllocationsField categoryOptions={categoryOptions} totalValue={totalValue} allocations={allocations} setAllocations={setAllocations} />

            {!editingEntry && (
                <div className="fl-row">
                    <div className="fl-col-narrow form-group">
                        <Input text="Parcelas" type="number" name="installments" placeholder="1" handleOnChange={(e) => setInstallments(e.target.value)} value={installments} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input text="Intervalo entre parcelas (dias)" type="number" name="intervalDays" placeholder="30" handleOnChange={(e) => setIntervalDays(e.target.value)} value={intervalDays} disabled={parseInt(installments) <= 1} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input text="1º Vencimento" type="date" name="firstDueDate" handleOnChange={(e) => setFirstDueDate(e.target.value)} value={firstDueDate} />
                    </div>
                </div>
            )}
        </FormLayout>
    )
}

export default FormFinanceEntry
